import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, ExternalLink, Loader2, MapPin, MessageCircle, Navigation } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { BoyShell } from "@/components/boy/BoyShell";
import { API, api, brl, errMsg, waLink } from "@/lib/api";
import { fmtKm, mapsLink, RIDE_STATUS } from "@/lib/boy";
import { useCourierGps } from "@/lib/tracking";
import { GpsBanner } from "@/components/tracking/GpsBanner";

const Stop = ({ title, text, href, dot, testId }) => (
  <div className="flex items-start gap-3" data-testid={testId}>
    {dot ? <span className="mt-1 h-3.5 w-3.5 shrink-0 rounded-full border-2 border-primary" /> : <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />}
    <div className="min-w-0 flex-1"><p className="eyebrow">{title}</p><p className="text-sm">{text}</p></div>
    <a href={href || mapsLink(text)} target="_blank" rel="noreferrer" className="flex shrink-0 items-center gap-1 text-xs font-medium text-primary hover:underline">Mapa<ExternalLink className="h-3 w-3" /></a>
  </div>
);

export default function BoyRide() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/public/boy-ride/${token}`).then((r) => setD(r.data)).catch((e) => setErr(errMsg(e)));
  }, [token]);
  const gps = useCourierGps(`/public/boy-ride/${token}/position`, d?.status === "andamento");
  const finish = async () => {
    if (!window.confirm("Confirmar que a entrega foi feita?")) return;
    setBusy(true);
    try {
      setD((await api.post(`/public/boy-ride/${token}/delivered`)).data);
      toast.success("Entrega finalizada!");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  if (err) return <BoyShell><p className="card-surface p-6 text-center text-sm text-muted-foreground" data-testid="boy-ride-error">{err}</p></BoyShell>;
  if (!d) return <BoyShell><p className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></p></BoyShell>;
  return (
    <BoyShell info={{ store: d.store }}>
      <div className="card-surface space-y-5 p-6" data-testid="boy-ride-card">
        <div className="flex items-center justify-between gap-2"><p className="text-2xl font-bold">Corrida #{d.number}</p><span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-semibold" data-testid="boy-ride-page-status">{RIDE_STATUS[d.status]}</span></div>
        <GpsBanner st={gps} />
        <Stop title="Retirada" text={d.origin} href={d.origin_map} dot testId="boy-ride-origin" />
        <Button asChild variant="outline" className="h-12 w-full text-base">
          <a href={d.origin_map || mapsLink(d.origin)} target="_blank" rel="noreferrer" data-testid="boy-ride-store-location-btn"><Navigation className="mr-2 h-5 w-5" />Abrir a localização da loja</a>
        </Button>
        <Stop title="Entrega" text={d.dest} href={d.dest_map} testId="boy-ride-dest" />
        <div className="grid grid-cols-3 gap-2 rounded-xl bg-muted/60 p-3 text-center">
          <div><p className="text-[11px] text-muted-foreground">Distância</p><p className="font-semibold">{fmtKm(d.distance_km)} km</p></div>
          <div><p className="text-[11px] text-muted-foreground">Valor</p><p className="font-semibold">{brl(d.price)}</p></div>
          <div><p className="text-[11px] text-muted-foreground">Você recebe</p><p className="font-bold text-emerald-600" data-testid="boy-ride-courier-value">{brl(d.courier_value)}</p></div>
        </div>
        <div className="flex items-center justify-between gap-2 text-sm">
          <span>Lojista: <b>{d.merchant}</b></span>
          <a href={waLink(d.merchant_whatsapp, `Olá, sou o motoboy da corrida #${d.number}`)} target="_blank" rel="noreferrer" className="flex items-center gap-1 font-medium text-emerald-600 hover:underline" data-testid="boy-ride-merchant-wa"><MessageCircle className="h-4 w-4" />WhatsApp</a>
        </div>
        {d.notes && <p className="rounded-md border-l-4 border-primary bg-primary/5 p-3 text-sm" data-testid="boy-ride-notes">{d.notes}</p>}
        {d.has_photo && <img src={`${API}/public/boy-ride/${token}/photo`} alt="Foto do pedido" className="max-h-72 w-full rounded-lg border object-contain" data-testid="boy-ride-photo" />}
        {d.status === "andamento" ? (
          <Button className="h-14 w-full text-base font-semibold" onClick={finish} disabled={busy} data-testid="boy-ride-finish-btn">
            {busy ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <CheckCircle2 className="mr-2 h-5 w-5" />}Finalizar entrega
          </Button>
        ) : <p className="text-center text-sm text-muted-foreground" data-testid="boy-ride-final-msg">{d.status === "entregue" ? "Entrega finalizada. Obrigado!" : "Esta corrida foi cancelada."}</p>}
      </div>
    </BoyShell>
  );
}
